// src/pages/AdminRolesPage.js
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Button, Form, Modal, Spinner, Table,
} from "react-bootstrap";
import api from "../api";

export default function AdminRolesPage() {
  const [roles, setRoles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [actionError, setActionError] = useState("");

  const [modal, setModal]         = useState(null); // "add" | "edit" | "delete"
  const [target, setTarget]       = useState(null); // role being acted on
  const [name, setName]           = useState("");
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.get("/admin/roles");
      setRoles(res.data.items);
    } catch {
      setError("Failed to load roles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openAdd() {
    setName("");
    setActionError("");
    setTarget(null);
    setModal("add");
  }

  function openEdit(role) {
    setName(role.name);
    setActionError("");
    setTarget(role);
    setModal("edit");
  }

  function openDelete(role) {
    setActionError("");
    setTarget(role);
    setModal("delete");
  }

  function closeModal() {
    setModal(null);
    setActionError("");
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!name.trim()) { setActionError("Role name is required."); return; }
    setSaving(true);
    setActionError("");
    try {
      await api.post("/admin/roles", { name: name.trim() });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to create role.");
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(e) {
    e.preventDefault();
    if (!name.trim()) { setActionError("Role name is required."); return; }
    setSaving(true);
    setActionError("");
    try {
      await api.put(`/admin/roles/${target.id}`, { name: name.trim() });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to update role.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    setActionError("");
    try {
      await api.delete(`/admin/roles/${target.id}`);
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to delete role.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex align-items-center mb-4 gap-3">
        <Link to="/admin" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1" />Back
        </Link>
        <h3 className="mb-0">
          <i className="bi bi-shield-lock-fill me-2" />
          Roles
        </h3>
        <Button size="sm" className="ms-auto" onClick={openAdd}>
          <i className="bi bi-plus-lg me-1" />Add Role
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table hover responsive size="sm">
        <thead>
          <tr>
            <th>Role Name</th>
            <th>Users</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {roles.map((r) => (
            <tr key={r.id}>
              <td className="align-middle fw-semibold">{r.name}</td>
              <td className="align-middle">
                <Badge bg={r.user_count > 0 ? "secondary" : "light"} text={r.user_count > 0 ? undefined : "muted"}>
                  {r.user_count}
                </Badge>
              </td>
              <td className="align-middle">
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-primary" onClick={() => openEdit(r)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={() => openDelete(r)}>
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Add Modal */}
      <Modal show={modal === "add"} onHide={closeModal} centered>
        <Form onSubmit={handleAdd}>
          <Modal.Header closeButton>
            <Modal.Title>Add Role</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group>
              <Form.Label>Role Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. VOLUNTEER"
                autoFocus
              />
              <Form.Text className="text-muted">Will be saved in uppercase.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Create Role"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal show={modal === "edit"} onHide={closeModal} centered>
        <Form onSubmit={handleEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Role</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group>
              <Form.Label>Role Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
              <Form.Text className="text-muted">Will be saved in uppercase.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={modal === "delete"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError
            ? <Alert variant="danger" className="py-2">{actionError}</Alert>
            : (
              <p className="mb-0">
                Delete role <strong>{target?.name}</strong>?
                {target?.user_count > 0 && (
                  <span className="text-danger d-block mt-2">
                    <i className="bi bi-exclamation-triangle-fill me-1" />
                    {target.user_count} user(s) currently have this role and must be reassigned first.
                  </span>
                )}
              </p>
            )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Cancel</Button>
          {!actionError && (
            <Button variant="danger" onClick={handleDelete} disabled={saving || target?.user_count > 0}>
              {saving ? <Spinner animation="border" size="sm" /> : "Delete"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
